import React, { useEffect, useRef, useState } from 'react';
import './monster-cart-v2-announcement.css';

import volumeTiers from 'assets/mcv2/volume-tiers.png';
import oneTickUpsell from 'assets/mcv2/one-tick-upsell.png';
import freeGiftStack from 'assets/mcv2/free-gift-stack.png';
import freeGiftSingle from 'assets/mcv2/free-gift-single.png';
import designClassicStack from 'assets/mcv2/design-classic-stack.png';
import designSwipe from 'assets/mcv2/design-swipe.png';
import designGlide from 'assets/mcv2/design-glide.png';
import designDiscounted from 'assets/mcv2/design-discounted.png';
import upsellTargeting from 'assets/mcv2/upsell-targeting.png';
import abTestingPanel from 'assets/mcv2/ab-testing-panel.png';
import abTestingResults from 'assets/mcv2/ab-testing-results.png';

const FAQ_URL =
    'https://monsterapps.crisp.help/en/article/monster-cart-v2-upgrade-faq-1klrtyc/';
const HELP_DESK_URL =
    'https://monsterapps.crisp.help/en/category/monster-cart-upsells-1dsps6d/';

// The modal auto-opens at most once per calendar day; the banner that replaces
// it can be dismissed for the rest of the browser session.
const MODAL_SEEN_DAY_KEY = 'mcv2_announcement_seen_day';
const BANNER_DISMISSED_KEY = 'mcv2_banner_dismissed';

// Storage can throw inside the embedded admin iframe (blocked third-party
// storage) — in that case the modal simply shows every visit.
function safeGet(storage, key) {
    try {
        return window[storage].getItem(key);
    } catch (e) {
        return null;
    }
}
function safeSet(storage, key, value) {
    try {
        window[storage].setItem(key, value);
    } catch (e) {
        /* noop */
    }
}

function todayKey() {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

// icon: 16x16 path data, drawn in the brand colour by the CSS
const FEATURES = [
    { icon: 'M1.5 2h1.8l1.9 8.2h7.4M4.4 8.7h8.2l1.4-4.6H3.4', title: 'Product page upsells', tag: 'New', body: 'Offers now run on product pages, not just in the cart.' },
    { icon: 'M8 1.5l2 4.1 4.5.6-3.3 3.2.8 4.5L8 11.7l-4 2.2.8-4.5L1.5 6.2 6 5.6z', title: 'Bundles with volume discounts', tag: 'New', body: 'Two and three-packs on the product page and in cart, gift optional.' },
    { icon: 'M2 5.5h12M2 5.5v8.2h12V5.5M2 5.5l1.2-2.4h9.6L14 5.5M8 3.1v10.6', title: 'Free gift with purchase', tag: 'New', body: 'Gifts on product pages and in cart, five cart layouts.' },
    { icon: 'M8 1.7a6.3 6.3 0 100 12.6A6.3 6.3 0 008 1.7zm0 3.7a2.6 2.6 0 110 5.2 2.6 2.6 0 010-5.2z', title: 'Advanced FBT targeting', tag: 'New', body: 'Rules decide the add-ons: if the cart has X, offer Y.' },
    { icon: 'M2.6 1.8h5.1l6.5 6.5-5.9 5.9L1.8 7.7V2.6zM5.2 5.2h.01', title: 'Discount your upsells', tag: 'New', body: 'Take a percentage off any add-on — the saving shows on the offer.' },
    { icon: 'M2 4.2h5.4M2 8h9.5M2 11.8h6.8', title: 'FBT layouts & placement', body: 'Five layouts, three cart positions, up to five add-ons.' },
    { icon: 'M8 1.5a6.5 6.5 0 000 13c1 0 1.6-.6 1.6-1.4 0-.7-.5-1-.5-1.6 0-.6.5-1 1.1-1h1.3c1.6 0 2.9-1.3 2.9-3 0-3.3-2.9-6-6.4-6z', title: 'New design editor', body: 'Style every widget live, no CSS and no support ticket.' },
    { icon: 'M1.8 14.2h12.4M4 14.2V7.6M7.4 14.2V3.4M10.8 14.2V9.6M14 14.2V5.6', title: 'Rebuilt analytics', body: 'See what each campaign actually earns you.' },
    { icon: 'M8.6 1.5H2.2v6.4l6 6a1.4 1.4 0 002 0l4.4-4.4a1.4 1.4 0 000-2z', title: 'New discount engine', tag: 'New', body: 'Native Shopify discounts — far fewer checkout conflicts.' },
    { icon: 'M2.4 7.2h11.2v7.1H2.4zM5.2 7.2V5a2.8 2.8 0 015.6 0', title: 'Add to Unlock, rebuilt', body: 'Free shipping, gift or discount goals with live progress.' },
    { icon: 'M2.6 3.4h4.1v4.1H2.6zM9.3 8.5h4.1v4.1H9.3zM9.3 5.4h4.6', title: 'Auto-add or let them pick', body: 'Gifts drop in automatically, or customers choose their own.' },
    { icon: 'M1.7 3.6h12.6v8.8H1.7zM4.4 8l1.7 1.8 3.4-3.6', title: 'New one-tick upsell designs', tag: 'New', body: 'Add-on offers that take a single tap in the cart.' },
    { icon: 'M8.9 1.4L3.1 9.1h4L6.6 14.6l6.3-8.1H8.4z', title: 'Faster on every store', body: 'Lighter cart, quicker paint, less theme interference.' },
    { icon: 'M8 1.6a6.4 6.4 0 100 12.8A6.4 6.4 0 008 1.6zM8 4.3v3.9l2.5 1.5', title: 'Subscriptions & Markets', body: 'Subscribe-in-cart and multi-currency, carried across.' },
    { icon: 'M6.2 1.8v4L2.6 12a1.6 1.6 0 001.4 2.4h8a1.6 1.6 0 001.4-2.4L9.8 5.8v-4M5.2 1.8h5.6M4.2 9.4h7.6', title: 'A/B testing', tag: 'Beta', body: 'Split traffic between two campaign versions — live in beta on every V2 store.' }
];

const SHOTS = [
    {
        src: volumeTiers,
        alt: 'Buy more save more tiers on a product page: Single, Duo saving 10%, Trio saving 15% with a free gift included.',
        power: 'Bundle with volume discounts',
        isNew: true,
        gain: 'Two and three-packs on the product page and in cart — with a free gift on the top tier.'
    },
    {
        src: oneTickUpsell,
        alt: 'One-tick upsell row in the cart with a toggle to add a product for $29.00, above the checkout button.',
        power: 'New one-tick upsell designs',
        gain: 'One toggle above checkout adds the offer — no extra page, no friction.'
    },
    {
        src: freeGiftStack,
        alt: 'Free gift widget under the Add to cart button offering a travel mug, notebook set or phone case, each marked Free.',
        power: 'Stack gifts to lift conversion',
        isNew: true,
        gain: 'Gift with purchase on product pages — offer several and let shoppers choose.'
    },
    {
        src: freeGiftSingle,
        alt: 'Single free gift card on a product page with a Value £14 pill and the word Free.',
        power: 'Or keep it to one gift',
        gain: 'Same conversion lift, simpler ask — five layouts ship, you pick.'
    }
];

const DESIGNS = [
    {
        src: designClassicStack,
        alt: 'Frequently Bought Together upsell showing two products stacked in a plain list, each with an Add + button.',
        name: 'Classic stack',
        detail: 'Every add-on listed, no scrolling'
    },
    {
        src: designSwipe,
        alt: 'Frequently Bought Together upsell as a card with the next card peeking in from the right edge.',
        name: 'Swipe',
        detail: 'Next card peeks in to invite a swipe'
    },
    {
        src: designGlide,
        alt: 'Frequently Bought Together upsell card with navigation arrows on the left and right.',
        name: 'Glide',
        detail: 'Arrows either side of the card'
    },
    {
        src: designDiscounted,
        alt: 'Frequently Bought Together upsell with header arrows, a struck-through price and a 10% off badge.',
        name: 'Discounted upsell',
        detail: 'Take % off an add-on — badge shows the saving'
    }
];

export default function MonsterCartV2Announcement({ onUpgrade, isUpgrading, upgradeError }) {
    const [isModalVisible, setIsModalVisible] = useState(
        () => safeGet('localStorage', MODAL_SEEN_DAY_KEY) !== todayKey()
    );
    const [isBannerDismissed, setIsBannerDismissed] = useState(
        () => safeGet('sessionStorage', BANNER_DISMISSED_KEY) === '1'
    );
    const dialogRef = useRef(null);

    // Same escape hatch the old modal exposed — lets support / other screens
    // reopen the announcement (window.showUpgradeModal(true)).
    window.showUpgradeModal = setIsModalVisible;

    // Mark the day as soon as the modal auto-shows, so reloads inside the
    // same day (or session) never bring it back.
    useEffect(() => {
        if (isModalVisible) safeSet('localStorage', MODAL_SEEN_DAY_KEY, todayKey());
    }, [isModalVisible]);

    useEffect(() => {
        if (!isModalVisible) return undefined;
        if (dialogRef.current) dialogRef.current.focus();
        const onKeyDown = (event) => {
            if (event.key === 'Escape') dismissModal();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalVisible, isUpgrading]);

    function dismissModal() {
        if (isUpgrading) return;
        setIsModalVisible(false);
    }

    function dismissBanner() {
        safeSet('sessionStorage', BANNER_DISMISSED_KEY, '1');
        setIsBannerDismissed(true);
    }

    const upgradeButton = (extraClass) => (
        <button
            type="button"
            className={`mcv2-btn ${extraClass || ''}`}
            disabled={isUpgrading}
            onClick={onUpgrade}
        >
            {isUpgrading ? 'Switching…' : 'Switch to V2'}
        </button>
    );

    const banner = !isModalVisible && !isBannerDismissed && (
        <div className="mcv2-banner-slot">
            <div className="mcv2-banner">
                <span className="mcv2-banner-dot" aria-hidden="true" />
                <span className="mcv2-banner-text">
                    <b>Monster Cart V1 is being phased out.</b> Switch to V2 to keep
                    getting new features — product page gifts and volume tiers are
                    already live.
                </span>
                {upgradeButton()}
                <button
                    type="button"
                    className="mcv2-btn mcv2-btn--plain"
                    onClick={dismissBanner}
                >
                    Dismiss
                </button>
                {upgradeError && <p className="mcv2-error">{upgradeError}</p>}
            </div>
        </div>
    );

    if (!isModalVisible) return banner || null;

    return (
        <div
            className="mcv2-backdrop"
            onMouseDown={(event) => {
                // Backdrop click, but only the backdrop itself — not a click
                // inside the dialog.
                if (event.target === event.currentTarget) dismissModal();
            }}
        >
            <div
                className="mcv2-dialog mcv2"
                role="dialog"
                aria-modal="true"
                aria-labelledby="mcv2-title"
                tabIndex={-1}
                ref={dialogRef}
            >
                <div className="mcv2-dialog-head">
                    <h2 className="mcv2-dialog-title" id="mcv2-title">
                        Monster Cart V2 is here
                    </h2>
                    <button
                        type="button"
                        className="mcv2-x"
                        aria-label="Close"
                        onClick={dismissModal}
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path
                                d="M2 2l10 10M12 2L2 12"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                {/* Only this band scrolls */}
                <div className="mcv2-dialog-body">
                    <div className="mcv2-hero">
                        <span className="mcv2-chip">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                <path
                                    d="M5 .8l1.3 2.7 2.9.4-2.1 2 .5 2.9L5 7.4 2.4 8.8l.5-2.9-2.1-2 2.9-.4z"
                                    fill="currentColor"
                                />
                            </svg>
                            Version 2
                        </span>
                        <h3>We rebuilt the whole thing</h3>
                        <p>
                            Same cart — faster, with more powers and many more ways to
                            boost average order value. Introducing product page widgets,
                            new free gift campaigns and new design options.
                        </p>
                    </div>

                    <div className="mcv2-phaseout" role="status">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M8 1.6L15 14H1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                            <path d="M8 6v3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            <circle cx="8" cy="11.8" r=".9" fill="currentColor" />
                        </svg>
                        <p>
                            <b>Monster Cart V1 is being phased out.</b> New features and
                            fixes now land in V2 only, and V1 will eventually be retired.
                            Switching takes minutes, your setup carries over, and support
                            will help if anything looks off.
                        </p>
                    </div>

                    <div className="mcv2-stack">
                        <p className="mcv2-label">New powers, live on your store</p>
                        <div className="mcv2-previews">
                            {SHOTS.map((shot) => (
                                <figure className="mcv2-shot" key={shot.power}>
                                    <img src={shot.src} loading="lazy" alt={shot.alt} />
                                    <figcaption>
                                        <span className="mcv2-shot-power">
                                            {shot.power}
                                            {shot.isNew && <span className="mcv2-shot-new">New</span>}
                                        </span>
                                        <span className="mcv2-shot-gain">{shot.gain}</span>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>

                    <div className="mcv2-stack">
                        <p className="mcv2-label">New upsell designs</p>
                        <div className="mcv2-designs">
                            {DESIGNS.map((design) => (
                                <figure className="mcv2-design" key={design.name}>
                                    <img src={design.src} loading="lazy" alt={design.alt} />
                                    <figcaption>
                                        {design.name}
                                        <span>{design.detail}</span>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                        <p className="mcv2-note">
                            Four ways to show your Frequently Bought Together offers —
                            pick per campaign, then restyle in the new editor. One-tick
                            upsells carry their own design options.
                        </p>
                    </div>

                    <div className="mcv2-stack">
                        <p className="mcv2-label">More upsell targeting</p>
                        <div className="mcv2-targeting">
                            <img
                                src={upsellTargeting}
                                loading="lazy"
                                alt="Add upsell panel: Monster Tee mapped to Monster Upsells Notebook at 10% off, and All products mapped to 4 selected products with an Add discount link."
                            />
                        </div>
                        <p className="mcv2-note">
                            Decide exactly which add-ons appear: match them to a specific
                            product, or offer them on everything — each with its own
                            discount.
                        </p>
                    </div>

                    <div className="mcv2-stack">
                        <p className="mcv2-label">
                            A/B test your campaigns{' '}
                            <span className="mcv2-tag mcv2-tag--beta">Beta</span>
                        </p>
                        <div className="mcv2-targeting">
                            <img
                                src={abTestingPanel}
                                loading="lazy"
                                alt="A/B Testing panel with A and B variant tabs, an Add variant button and a Settings button, and the note: select a tab to edit that variant, every arm is saved together when you save the campaign."
                            />
                        </div>
                        <p className="mcv2-note">
                            Build two variants of any campaign — same setup, different
                            offer.
                        </p>
                        <div className="mcv2-targeting">
                            <img
                                src={abTestingResults}
                                loading="lazy"
                                alt="Campaign list with an A/B test badge, expanded to show Variant A and Variant B each at 50% of traffic, with views, orders, revenue per view and campaign revenue compared side by side."
                            />
                        </div>
                        <p className="mcv2-note">
                            See views, orders and revenue per view for each variant side
                            by side, then keep the one that earns more. Live in beta on
                            every V2 store.
                        </p>
                    </div>

                    <div className="mcv2-stack">
                        <p className="mcv2-label">Everything V2 adds</p>
                        <div className="mcv2-features">
                            {FEATURES.map((feature) => (
                                <div className="mcv2-feature" key={feature.title}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path
                                            d={feature.icon}
                                            stroke="currentColor"
                                            strokeWidth="1.3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div>
                                        <b>
                                            {feature.title}
                                            {feature.tag && (
                                                <span
                                                    className={`mcv2-tag${
                                                        feature.tag !== 'New'
                                                            ? ` mcv2-tag--${feature.tag.toLowerCase()}`
                                                            : ''
                                                    }`}
                                                >
                                                    {feature.tag}
                                                </span>
                                            )}
                                        </b>
                                        <span>{feature.body}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mcv2-stack">
                        <p className="mcv2-label">What happens when you hit Switch</p>
                        <ol className="mcv2-steps">
                            <li>
                                <div>
                                    <b>Your new cart starts switched off.</b>{' '}
                                    <span>
                                        Customers keep checking out with your theme&apos;s
                                        standard cart while you get set up — nothing changes
                                        on your storefront yet.
                                    </span>
                                </div>
                            </li>
                            <li>
                                <div>
                                    <b>You review everything.</b>{' '}
                                    <span>
                                        Your campaigns and settings carry over, but expect
                                        slight design changes — fonts and spacing can sit a
                                        little differently. Check each widget and tune it in
                                        the new editor.
                                    </span>
                                </div>
                            </li>
                            <li>
                                <div>
                                    <b>You turn it on when it&apos;s right.</b>{' '}
                                    <span>
                                        Nothing goes live until you flip the switch.
                                        You&apos;re never live with a cart you haven&apos;t
                                        looked at.
                                    </span>
                                </div>
                            </li>
                        </ol>
                    </div>

                    <div className="mcv2-carryover">
                        <p className="mcv2-label">Everything V1 did, it still does</p>
                        <ul className="mcv2-chips">
                            <li>Free gifts</li>
                            <li>Frequently Bought Together</li>
                            <li>Volume discounts</li>
                            <li>Discount codes in cart</li>
                            <li>Order notes</li>
                            <li>Custom CSS</li>
                            <li>Developer events &amp; selectors</li>
                        </ul>
                        <p className="mcv2-note">
                            Every V1 guide you&apos;ve ever bookmarked is still in the{' '}
                            <a href={HELP_DESK_URL} target="_blank" rel="noopener noreferrer">
                                help desk
                            </a>
                            , alongside the new V2 articles.
                        </p>
                    </div>

                    <p className="mcv2-note mcv2-note--info">
                        …and a good deal more we couldn&apos;t fit in a box. The{' '}
                        <a href={FAQ_URL} target="_blank" rel="noopener noreferrer">
                            V2 upgrade FAQ
                        </a>{' '}
                        covers what changes for your store, and support is one message
                        away if you&apos;d rather just ask.
                    </p>
                </div>

                {/* Never scrolls: the upgrade button is reachable at every scroll position */}
                <div className="mcv2-dialog-foot">
                    <a
                        className="mcv2-foot-link"
                        href={FAQ_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        See the full V2 guide
                    </a>
                    <button
                        type="button"
                        className="mcv2-btn mcv2-btn--plain"
                        disabled={isUpgrading}
                        onClick={dismissModal}
                    >
                        Not right now
                    </button>
                    {upgradeButton('mcv2-btn--primary')}
                    {upgradeError && <p className="mcv2-error">{upgradeError}</p>}
                </div>
            </div>
        </div>
    );
}
